'use client';

import { useState } from 'react';

import { ArrowLeft, Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

import Link from 'next/link';
import Image from 'next/image';

import { useRouter, useSearchParams } from 'next/navigation';
import { useSignIn } from '@clerk/nextjs';

export default function LoginForm() {
    const router = useRouter();
    const { isLoaded, signIn, setActive } = useSignIn();
    const searchParams = useSearchParams();
    const redirectUrl = searchParams.get('redirect_url');
    const safeRedirectUrl = redirectUrl && redirectUrl.startsWith('/dashboard') ? redirectUrl : '/dashboard';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!isLoaded) {
            return;
        }

        setIsLoading(true);

        try {
            const result = await signIn.create({
                identifier: email,
                password,
            });

            if (result.status === 'complete') {
                await setActive({ session: result.createdSessionId });
                router.push(safeRedirectUrl);
            } else {
                // Étapes supplémentaires requises (MFA, etc.)
                setError('Une étape supplémentaire est requise pour la connexion.');
            }
        } catch (err: unknown) {
            // Message volontairement générique pour ne pas révéler 
            // si l'email existe ou non dans le système.
            setError('Échec de la connexion. Veuillez vérifier vos informations.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-linear-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">

                <div className="mb-6">
                    <Link href="/" className="flex items-center gap-2 text-gray-700">
                        <ArrowLeft className="h-4 w-4" />
                        Retour à l&apos;accueil
                    </Link>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="flex justify-center mb-6">
                        <Image src="/img/Fluidexpert-full-dark-250px.png" alt="Fluidexpert Logo" width={250} height={100} className="object-contain h-auto w-auto" />
                    </div>
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Se connecter</h2>
                        <p className="text-sm text-gray-600 mb-6 text-center">
                            Accédez à votre espace Fluidexpert
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="votre.email@exemple.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10"
                                    required
                                />
                            </div>
                            <Link
                                href="/forgot-password"
                                className="inline-block text-sm text-blue-600 hover:text-blue-700 underline underline-offset-2"
                            >
                                Mot de passe oublié ?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Connexion en cours...' : 'Se connecter'}
                        </Button>

                        {error && (
                            <p className="text-sm text-red-600 text-center">
                                {error}
                            </p>
                        )}

                        <div className="mt-6 text-center">
                            <p className="text-sm text-gray-600">
                                Pas encore de compte ?{' '}
                                <Link href="/register" className="text-blue-600 hover:text-blue-700 font-medium">
                                    S&apos;inscrire
                                </Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
